import { BindingEngine, Disposable } from 'aurelia-framework';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Subject } from 'rxjs/internal/Subject';
import { combineLatestWith } from 'rxjs/internal/operators/combineLatestWith';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { tap } from 'rxjs/internal/operators/tap';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { CustomerService } from '../services/customer-service';
import { IApplicationFilter } from '../interfaces/i-application-filter';
import { property } from './property-decorator';
import { WidgetPropertyType } from './widget-property-type';
import { IPropertyMetadata } from './i-property-metadata';
import { getWidgetPropertiesMetadata } from '../services/widget-service';
import { delayWhen } from 'rxjs/internal/operators/delayWhen';
import { MdcMenu } from '@aurelia-mdc-web/menu';
import { IExcelExportData } from '../interfaces/i-excel-export-data';

let id = 1;

export abstract class WidgetBase implements EventListenerObject {
  constructor(protected element: Element, protected bindingEngine: BindingEngine, protected customerService: CustomerService) { }

  id = ++id;
  gridReady$: Subject<void> = new BehaviorSubject<void>(undefined);

  @property('Title')
  title?: string;

  @property()
  titleFont?: string;

  @property()
  titleFontSize?: number;

  @property()
  titleColor?: string;

  @property(undefined, undefined, WidgetPropertyType.List, [{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }])
  titleAlign?: 'left' | 'center' | 'right';

  @property()
  borderWidth?: string;

  @property()
  borderColor?: string;

  @property('Border')
  padding?: string;

  @property(null)
  parameterisedProperties?: Record<string, string | undefined> = {};

  subs: Disposable[];
  refresh$ = new BehaviorSubject<void>(undefined);
  destroy$ = new Subject<void>();
  loading = true;
  error?: Error;
  printed = false;
  properties: IPropertyMetadata[];
  isInDashboard: boolean;
  excelData: IExcelExportData | null = null;

  activate(model: unknown) {
    Object.assign(this, model);
    // need to create a copy to avoid sharing the same object between widget API data and widget instance
    this.parameterisedProperties = { ...this.parameterisedProperties };
  }

  /** Called in attached */
  protected async initialise(): Promise<void> { }

  protected async destroy(): Promise<void> { }

  protected async loadData(filter: IApplicationFilter): Promise<unknown> {
    return Promise.resolve();
  }

  protected configChanged(key: string, newValue: any, oldValue: any) { }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  protected async setData(data: unknown): Promise<void> { }

  getPageCount?(): number;
  setPage?(page: number): Promise<void>;

  async showContextMenu(event: MouseEvent) {
    // AG Grid opens its own context menu (with Excel export) for the same event.
    if (!this.excelData || (event.target as Element).closest?.('.ag-root-wrapper')) {
      return;
    }

    this.customerService.excelExportMenuAnchor.style.left = `${event.clientX}px`;
    this.customerService.excelExportMenuAnchor.style.top = `${event.clientY}px`;
    event.preventDefault();
    if (this.customerService.exportMenu.open) {
      this.customerService.exportMenu.open = false;
      await Promise.delay(100);
    }

    this.customerService.excelData = this.excelData;
    this.customerService.exportMenu.openAnchored(this.customerService.excelExportMenuAnchor);
  }

  async attached() {
    await this.initialise();
    this.isInDashboard = !this.element.closest('.report-page');
    this.properties = getWidgetPropertiesMetadata(this.constructor);
    this.subscribeToFilter();
    this.subs = this.properties
      .map(x => this.bindingEngine.propertyObserver(this, x.name)
        .subscribe((newValue, oldValue) => this.configChanged(x.name, newValue, oldValue)));
    this.element.addEventListener('contextmenu', this);
    this.element.addEventListener('click', this);
  }

  handleEvent(object: Event): void {
    switch (object.type) {
      case 'contextmenu':
        this.showContextMenu(object as MouseEvent);
        break;
      case 'click':
        this.customerService.exportMenu.open = false;
        break;
    }
  }

  async detached() {
    await this.destroy();
    this.subs.forEach(x => x.dispose());
    this.destroy$.next();
    this.destroy$.complete();
    this.element.removeEventListener('contextmenu', this);
    this.element.removeEventListener('click', this);
  }

  notifyLoading() {
    this.loading = true;
  }

  notifyLoaded() {
    this.loading = false;
  }

  /** Called after initialised, in attached */
  protected subscribeToFilter() {
    this.customerService.customerAndDates$
      .pipe(
        delayWhen(() => this.gridReady$),
        combineLatestWith(this.refresh$),
        debounceTime(500),
        tap(() => {
          this.error = undefined;
          this.notifyLoading();
        }),
        switchMap(async ([c]) => this.loadData(c).catch(e => e as Error)),
        takeUntil(this.destroy$)
      )
      .subscribe(async x => {
        if (x instanceof Error) {
          this.error = x;
        } else {
          await this.setData(x);
        }
        this.notifyLoaded();
      });
  }

  getParameterisedPropertiesMetadata() {
    return this.properties.filter(x => this.parameterisedProperties[x.name]).map(x => ({ ...x, parameterName: this.parameterisedProperties[x.name] }));
  }

}
